import React, { useState } from "react";
import { Modal, ModalBody } from "@velaro/velaro-component-lib";
import { SectionCard, PrimaryButton } from "@/components/ds";
import { AlertTriangle, IconClose, IconPlus } from "@velaro/velaro-component-lib";
import { Edit as EditIcon } from "@/components/ds/icons";
import { useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";

interface DynamicsConfig {
  id: number;
  siteId: number;
  displayName: string;
  tenantId: string;
  clientId: string;
  instanceUrl: string;
  orgName: string;
  enabled: boolean;
  createdAt: string;
  clientSecretMasked: string;
  clientSecretExpiresAt?: string | null;
  secretStatus?: "ok" | "expiring_soon" | "expired" | null;
}

function SecretExpiryBadge({ config }: { config: DynamicsConfig }) {
  if (!config.secretStatus || config.secretStatus === "ok") return null;
  const expired = config.secretStatus === "expired";
  const expDate = config.clientSecretExpiresAt
    ? new Date(config.clientSecretExpiresAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : null;
  return (
    <div className={`mt-1 flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full w-fit ${
      expired ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"
    }`}>
      <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
      </svg>
      {expired ? `Secret expired${expDate ? ` ${expDate}` : ""}` : `Secret expires ${expDate ?? "soon"}`}
    </div>
  );
}

interface Props {
  configs: DynamicsConfig[];
  onDelete(id: number): void;
  onRefresh(): void;
}

export default function DynamicsAccounts({ configs, onDelete, onRefresh }: Props) {
  const navigate = useNavigate();
  const api = useApi();
  const [deleteTarget, setDeleteTarget] = useState<DynamicsConfig | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [saving, setSaving] = useState(false);

  async function confirmDelete() {
    if (!deleteTarget) return;
    await api.admin.delete(`Dynamics/Configs/${deleteTarget.id}`);
    onDelete(deleteTarget.id);
    setDeleteTarget(null);
  }

  async function saveDisplayName(config: DynamicsConfig) {
    setSaving(true);
    try {
      await api.admin.put(`Dynamics/Configs/${config.id}`, { displayName: editName });
      onRefresh();
      setEditingId(null);
    } finally {
      setSaving(false);
    }
  }

  async function toggleEnabled(config: DynamicsConfig) {
    await api.admin.put(`Dynamics/Configs/${config.id}`, { enabled: !config.enabled });
    onRefresh();
  }

  return (
    <SectionCard title="Connected Orgs" className="mb-4">
      <div className="flex items-center justify-between mb-4">
        <div></div>
        <PrimaryButton
          label="Add Org"
          onClick={() => navigate("/integrations/dynamics/create")}
          prefixIcon={React.createElement(IconPlus)}
          className="font-medium"
        />
      </div>

      {configs.length === 0 ? (
        <div className="py-8 text-center">
          <div className="text-zinc-500 text-sm">No Dynamics 365 orgs connected yet.</div>
          <div className="text-zinc-400 text-xs mt-1">Click "Add Org" above to connect your Dynamics 365 environment.</div>
        </div>
      ) : (
        <table className="table-fixed w-full text-sm">
          <thead>
            <tr className="text-left text-zinc-500 text-xs border-b">
              <th className="pb-2 font-medium">Org / Display Name</th>
              <th className="pb-2 font-medium">Instance</th>
              <th className="pb-2 font-medium">Status</th>
              <th className="pb-2 w-24"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {configs.map((config) => (
              <tr key={config.id} className="py-2">
                <td className="py-3 pr-4">
                  {editingId === config.id ? (
                    <div className="flex gap-2">
                      <input
                        className="input text-sm w-full"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") saveDisplayName(config); if (e.key === "Escape") setEditingId(null); }}
                        autoFocus
                      />
                      <button onClick={() => saveDisplayName(config)} disabled={saving} className="text-blue-600 text-xs px-2">Save</button>
                      <button onClick={() => setEditingId(null)} className="text-zinc-400 text-xs px-1" aria-label="Cancel">x</button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-zinc-800">{config.displayName || config.orgName || "Dynamics 365"}</span>
                      <button
                        onClick={() => { setEditingId(config.id); setEditName(config.displayName); }}
                        className="text-zinc-400 hover:text-zinc-600 text-xs"
                        aria-label="Edit name"
                      ><EditIcon className="w-3.5 h-3.5" aria-hidden /></button>
                    </div>
                  )}
                  <div className="text-xs text-zinc-400 mt-0.5">{config.clientSecretMasked} · App {config.clientId?.slice(0, 8)}…</div>
                  <SecretExpiryBadge config={config} />
                </td>
                <td className="py-3 pr-4 text-zinc-500 text-xs truncate max-w-[180px]" title={config.instanceUrl}>
                  {config.instanceUrl?.replace("https://", "") || "—"}
                </td>
                <td className="py-3 pr-4">
                  <button
                    onClick={() => toggleEnabled(config)}
                    className={`text-xs px-2 py-0.5 rounded-full ${config.enabled ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}
                  >
                    {config.enabled ? "Active" : "Disabled"}
                  </button>
                </td>
                <td className="py-3 text-right">
                  <button
                    onClick={() => setDeleteTarget(config)}
                    className="text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded border border-red-200 hover:border-red-400"
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {deleteTarget && (
        <Modal show={true}>
          <ModalBody>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-6 text-red-500" />
                <span className="font-semibold text-zinc-800">Remove Org</span>
              </div>
              <button onClick={() => setDeleteTarget(null)}><IconClose className="w-6 text-zinc-400" /></button>
            </div>
            <p className="text-sm text-zinc-600 mb-6">
              Remove <strong>{deleteTarget.displayName || "this org"}</strong>? This cannot be undone.
            </p>
            <div className="flex justify-center gap-3">
              <button onClick={() => setDeleteTarget(null)} className="px-5 py-2 rounded-lg border border-blue-500 text-blue-600 text-sm font-medium">Cancel</button>
              <button onClick={confirmDelete} className="px-5 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700">Remove</button>
            </div>
          </ModalBody>
        </Modal>
      )}
    </SectionCard>
  );
}
